import { CommonModule, DatePipe, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import localePt from '@angular/common/locales/pt';
import {
  APP_INITIALIZER,
  importProvidersFrom,
  LOCALE_ID,
  NgModule
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import {
  MAT_SNACK_BAR_DEFAULT_OPTIONS,
  MatSnackBarModule
} from '@angular/material/snack-bar';
import { MAT_TABS_CONFIG } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BrowserModule, DomSanitizer } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateModule } from '@ngx-translate/core';
import { NgxMaskModule } from 'ngx-mask';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { CardModule } from 'primeng/card';
import { DialogModule } from 'primeng/dialog';
import { FloatLabelModule } from 'primeng/floatlabel';
import { StyleClassModule } from 'primeng/styleclass';
import { TimelineModule } from 'primeng/timeline';
import { TooltipModule } from 'primeng/tooltip';
import SwiperCore, {
  Autoplay,
  EffectCoverflow,
  Navigation,
  Pagination
} from 'swiper';
import { SwiperModule } from 'swiper/angular';
import {
  DomainService,
  UserService,
  UserSettingsService,
  ViggoCoreBreadCrumbModule,
  ViggoCoreChartModule,
  ViggoCoreDashboardDefaultModule,
  ViggoCoreFooterModule,
  ViggoCoreHeaderModule,
  ViggoCoreModule,
  ViggoCorePageAccessDeniedModule,
  ViggoCorePageErrorModule,
  ViggoCorePageNotFoundModule,
  ViggoCoreSideMenuModule,
  ViggoCoreToolBarModule,
  ViggoCoreWorkSpaceModule
} from 'viggo-sistemas-core-web';
import { environment } from '../environments/environment';
import {
  DashboardComponent,
  FirstStepsComponent,
  LoggedComponent
} from './app-components';
import { DashboardInfoComponent } from './app-components/dashboard/components/dashboard-info/dashboard-info.component';
import { DialogDashboardFiltrosComponent } from './app-components/dashboard/components/dialog-dashboard-filtros/dialog-dashboard-filtros.component';
import { DashboardInfoService } from './app-components/dashboard/service/dashboard.service';
import { AppComponent } from './app.component';
import { AnalyticsService, BuildDetailsHttpService, CustomMenuService } from './global services';
import { FastMenuService } from './global services/fast-menu-service/fast-menu-service';
import { FastCadastroModule } from './modules/fast-cadastro/fast-cadastro.module';
import { AppRoutingModule } from './routes/routes';
import { UnloggedGuard } from './shared/guards/unloged.guard';
import { AuthService } from './shared/services/auth.service';
import { CacheService } from './shared/services/cache.service';
import { GlobalHttpInterceptorService } from './shared/services/global-interceptor.service';
import { SharedModule } from './shared/shared.module';

export function fetchBuildDetails(buildDetailsService: BuildDetailsHttpService) {
  return () => buildDetailsService.fetchBuildDetails();
}

export function clearIndexHtmlCache(cacheService: CacheService) {
  return () => cacheService.clearCacheForFile('index.html');
}
registerLocaleData(localePt);
// install Swiper modules
SwiperCore.use([EffectCoverflow, Pagination, Autoplay, Navigation]);

@NgModule({
  declarations: [
    AppComponent,
    LoggedComponent,
    DashboardComponent,
    DashboardInfoComponent,
    FirstStepsComponent,
    DialogDashboardFiltrosComponent
  ],
  imports: [
    CommonModule,
    FormsModule,
    BrowserModule,
    HttpClientModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    // requerido import no raiz
    NgxMaskModule.forRoot({ validation: false }),
    // rotas
    AppRoutingModule,
    // CalendarModule.forRoot({
    //  provide: DateAdapter,
    //  useFactory: () => adapterFactory(moment)
    // useFactory: adapterFactory
    //  }),
    SharedModule,
    FastCadastroModule,
    ViggoCoreModule.forRoot({
      interceptors: {
        includeAuthInterceptor: { enable: true }
      }
    }),
    SwiperModule,
    // material modules
    MatSnackBarModule,
    MatIconModule,
    MatTooltipModule,
    MatButtonModule,
    MatDialogModule,
    MatCardModule,
    MatInputModule,
    MatDatepickerModule,
    MatMenuModule,
    // primeng modules
    CardModule,
    CalendarModule,
    TimelineModule,
    StyleClassModule,
    ButtonModule,
    TooltipModule,
    DialogModule,
    FloatLabelModule,
    // ViggoCore modulos
    ViggoCoreWorkSpaceModule,
    ViggoCoreDashboardDefaultModule,
    ViggoCoreFooterModule,
    ViggoCoreBreadCrumbModule,
    ViggoCorePageNotFoundModule,
    ViggoCoreSideMenuModule,
    ViggoCoreToolBarModule,
    ViggoCoreHeaderModule,
    ViggoCoreChartModule,
    ViggoCorePageErrorModule,
    ViggoCorePageNotFoundModule,
    ViggoCorePageAccessDeniedModule,
    //
    StoreModule.forRoot({}, {}),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: environment.production,
      connectInZone: true
    }),
    EffectsModule.forRoot([]),
    TranslateModule.forRoot()
  ],
  providers: [
    UserService,
    DomainService,
    CustomMenuService,
    FastMenuService,
    UnloggedGuard,
    DashboardInfoService,
    AuthService,
    CacheService,
    UserSettingsService,
    AnalyticsService,
    DatePipe,
    {
      provide: APP_INITIALIZER,
      useFactory: fetchBuildDetails,
      deps: [BuildDetailsHttpService],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: clearIndexHtmlCache,
      deps: [CacheService],
      multi: true
    },
    // WINDOW_PROVIDERS,
    { provide: LOCALE_ID, useValue: 'pt-BR' },
    {
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
      useValue: { duration: 4000 }
    },
    {
      provide: MAT_TABS_CONFIG,
      useValue: {
        animationDuration: '300ms'
      }
    },
    importProvidersFrom(HttpClientModule),
    // contasInterceptorProvider // TODO: ACESSE ESSE LINK E CRIE UM ARQUIVO COM TODOS OS INTERCEPTS DE TODOS OS MODULOS PAPAI https://v17.angular.io/guide/http-intercept-requests-and-responses
    {
      provide: HTTP_INTERCEPTORS,
      useClass: GlobalHttpInterceptorService,
      multi: true
    },
    provideAnimationsAsync('noop')
    // {provide: APP_BASE_HREF, useValue: '/src/app'}
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor(matIconRegistry: MatIconRegistry, domSanitizer: DomSanitizer) {
    // fazendo registro dos paths de icones
    matIconRegistry.addSvgIconSet(
      domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons.svg')
    );
  }
}
